import { Field } from 'formik';
import { APITypes } from '../../../../primary-record-types';
import React, { useState } from 'react';
import { getIntl } from '../../../../utils/intl';
import { required, zipCode, getDateFormatValidator } from '../../../../utils/validators';
import TempImagePicker, {
    TempAndPersistedAvatarState,
    avatarStateToAvatarChange,
    useTempAndExistingAvatarState,
} from '../../../v2BaseComponents/ImagePicker/TempImagePicker';
import urls from '../../../../utils/urls';
import { AVATAR_UPLOAD_QUALITY } from '../../../../utils/constants';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import { CenteredFormRow, Form, SplitFormRow } from '../../../v2BaseComponents/Form';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';

const intl = getIntl('accountInformation.userInfoForm', {
    accountInformation: 'Account Information',
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    dob: 'Date of Birth',
    preferredName: 'Preferred Name',
    phoneNumber: 'Phone Number',
    streetAddress1: 'Street Address 1',
    streetAddress2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    save: 'Save',
    avatarTitle: 'Account Photo',
});

export type UserInfoFormOwnProps = {
    onUpdate: (values: APITypes.userService.UpdateUserBody) => void;
    user: APITypes.userService.User;
    isDisabled?: boolean;
    isSubmitting?: boolean;
};

export type UserInfoFormProps = UserInfoFormOwnProps;

const UserInfoForm: React.FC<UserInfoFormProps> = ({ onUpdate, user, isDisabled, isSubmitting }) => {
    // @ts-expect-error TS2345
    const [avatarState, onAvatarStateUpdate] = useState<TempAndPersistedAvatarState>(null);
    const { tempImagePickerProps } = useTempAndExistingAvatarState({
        existingAvatarUrl: urls.api.users.avatar,
        avatarState,
        onAvatarStateUpdate,
    });
    return (
        <Form
            actionContainerSubmit
            isDisabled={isDisabled}
            initialValues={user}
            onSubmit={(values, helpers) => {
                onUpdate({
                    // `dob` is optional in the UI but the API schema requires the key (an empty
                    // string is allowed). Older user records have no `dob`, so it comes through
                    // as undefined; default to '' to avoid a spurious "user.dob: Required" error.
                    user: { ...values, dob: values.dob || '' },
                    avatar: avatarStateToAvatarChange(avatarState),
                });
                handleFormikSubmit(values, helpers);
            }}
        >
            {() => (
                <FormStack>
                    <CenteredFormRow>
                        <TempImagePicker
                            {...tempImagePickerProps}
                            isDisabled={isDisabled || isSubmitting}
                            title={intl('avatarTitle')}
                            imagePickerOptions={{
                                allowsEditing: true,
                                aspect: [1, 1],
                                quality: AVATAR_UPLOAD_QUALITY,
                            }}
                            sizeVariant="avatar"
                        />
                    </CenteredFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="email"
                            isRequired
                            title={intl('email')}
                            isDisabled={true} // edits not allowed
                        />
                        <Field
                            component={FormikInputs.Input}
                            name="firstName"
                            isRequired
                            title={intl('firstName')}
                            isDisabled={isDisabled || isSubmitting}
                            validate={required}
                        />
                    </SplitFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="lastName"
                            isRequired
                            title={intl('lastName')}
                            isDisabled={isDisabled || isSubmitting}
                            validate={required}
                        />
                        <Field
                            component={FormikInputs.DateTimePicker}
                            name="dob"
                            title={intl('dob')}
                            isDisabled={isSubmitting}
                            validate={getDateFormatValidator('MM/DD/YYYY')}
                        />
                    </SplitFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="preferredName"
                            title={intl('preferredName')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                        <Field
                            component={FormikInputs.PhoneNumber}
                            name="phoneNumber"
                            title={intl('phoneNumber')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                    </SplitFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="streetAddress1"
                            title={intl('streetAddress1')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                        <Field
                            component={FormikInputs.Input}
                            name="streetAddress2"
                            title={intl('streetAddress2')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                    </SplitFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="city"
                            title={intl('city')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                        <Field
                            component={FormikInputs.Input}
                            name="state"
                            title={intl('state')}
                            isDisabled={isDisabled || isSubmitting}
                        />
                    </SplitFormRow>
                    <SplitFormRow>
                        <Field
                            component={FormikInputs.Input}
                            name="zipCode"
                            title={intl('zipCode')}
                            isDisabled={isDisabled || isSubmitting}
                            validate={zipCode}
                        />
                    </SplitFormRow>
                </FormStack>
            )}
        </Form>
    );
};

export default React.memo(UserInfoForm);
